import { useState } from 'react';
import type { ReactElement } from 'react';
import { assetToast } from '@xdocs/i18n';
import type { PolicyCategory, TakedownTargetType } from '@xdocs/schemas';
import { Button, Modal, Select, Textarea, cn, useI18n, useToast } from '@xdocs/ui';
import { useSharingT } from './useSharingT.js';
import { useTakedown } from './useTakedown.js';
import styles from './TakedownDialog.module.css';

const POLICY_CATEGORIES: readonly PolicyCategory[] = ['illegal', 'hate', 'harassment', 'sexual', 'sexual_minors', 'violence', 'self_harm', 'malware', 'pii'];

export interface TakedownDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  shareId: string;
  targetType: TakedownTargetType;
  targetId: string;
  /** Named asset for the confirm/toast copy (§16.1 — never "this"). */
  assetName: string;
  onRemoved?: () => void;
}

/** F69 moderator/admin takedown — reason REQUIRED, drawn from the closed
 * `policyCategory` vocabulary (never invented), reversible by an admin,
 * always audited server-side. Matches design/app/34-share-public.html's
 * takedown confirm (reason select + optional audit note). */
export function TakedownDialog({ open, onOpenChange, shareId, targetType, targetId, assetName, onRemoved }: TakedownDialogProps): ReactElement {
  const { locale, t: tCommon } = useI18n();
  const { t } = useSharingT();
  const toast = useToast();
  const takedown = useTakedown(shareId);
  const [reasonCategory, setReasonCategory] = useState<PolicyCategory | ''>('');
  const [reasonNote, setReasonNote] = useState('');
  const [validationError, setValidationError] = useState<string | null>(null);

  async function handleConfirm(): Promise<void> {
    if (!reasonCategory) {
      setValidationError(t('sharing:moderator.takedown.reasonRequired'));
      return;
    }
    setValidationError(null);
    try {
      await takedown.takedown({ targetType, targetId, reasonCategory, ...(reasonNote.trim() ? { reasonNote: reasonNote.trim() } : {}) });
      toast.push(assetToast(locale, 'success', t('sharing:moderator.takedown'), assetName));
      onOpenChange(false);
      onRemoved?.();
    } catch {
      // `takedown.error` rendered inline below.
    }
  }

  return (
    <Modal open={open} onClose={() => onOpenChange(false)} title={t('sharing:moderator.takedown.title')} size="sm">
      <p className={cn(styles['description'])}>{t('sharing:moderator.takedown.description', { title: assetName })}</p>
      <Select
        label={t('sharing:moderator.takedown.reasonLabel')}
        required
        value={reasonCategory}
        {...(validationError ? { error: validationError } : {})}
        onChange={(event) => setReasonCategory(event.target.value as PolicyCategory)}
        placeholder={t('sharing:moderator.takedown.reasonPlaceholder')}
        options={POLICY_CATEGORIES.map((category) => ({ value: category, label: t(`sharing:policyCategory.${category}`) }))}
      />
      <Textarea
        label={t('sharing:moderator.takedown.noteLabel')}
        value={reasonNote}
        onChange={(event) => setReasonNote(event.target.value)}
      />
      {takedown.error && (
        <p role="alert" className={cn(styles['error'])}>
          {takedown.error.message ?? t('sharing:moderator.takedown.failed')}
        </p>
      )}
      <div className={cn(styles['actions'])}>
        <Button variant="secondary" onClick={() => onOpenChange(false)} disabled={takedown.isPending}>
          {tCommon('common:action.cancel')}
        </Button>
        <Button variant="destructive" state={takedown.isPending ? 'loading' : 'idle'} onClick={() => void handleConfirm()}>
          {t('sharing:moderator.takedown.confirm')}
        </Button>
      </div>
    </Modal>
  );
}
